import AdyenWebCheckout from '@adyen/adyen-web'
import { Recaptcha } from 'app/common/recaptcha'
import React, { useCallback, useEffect, useState } from 'react'
import { isIOS, isSafari, osName } from 'react-device-detect'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
    actionAdyenPaymentResponse,
    actionAdyenRedirection,
    fetchAdyenPaymentMethods,
    priceSelector,
    sendAdyenPayment,
    sendCurrentOrder,
    setError,
    setLoader
} from 'shared/store/ecommerce/order'
import { fetchProfileData } from 'shared/store/kingdom/profile'
import { adyenPaymentMethods, PaymentProvider } from 'shared/utils'
import { getCurrentOrder } from 'shared/utils/ecommerce'
import { ErrorBanner } from '../../components'
import { CartSummary } from './CartSummaryContainer'
import { useApplePay, useGooglePay } from './hooks'

let recaptchaPromise: Promise<string>
let resolvePromise: any
let rejectPromise: any

export const isApplePayAvailable = isIOS || (osName === 'Mac OS' && isSafari)
export const isGooglePayAvailable = true

export const AdyenOneClickPaymentWrapper = (props: any) => {
    const kingdomProfilePageState = useSelector(
        state => state.kingdomProfilePageState
    )
    const { authenticated } = useSelector(state => state.userState)
    const orderState = useSelector(state => state?.ecommerceOrderState)
    const [checkout, setCheckout] = useState(null)
    const dispatch = useDispatch()
    const [showCaptcha, setShowCaptcha] = useState(false)
    const priceState = useSelector(priceSelector)
    const { error } = orderState
    const isAdyenRestaurant =
        orderState?.restaurant?.paymentServiceProvider === PaymentProvider.ADYEN
    const intl = useIntl()
    const history = useHistory()
    const [userData, setUserData] = useState(undefined)
    const [submitData, setSubmitData] = useState(undefined)

    const enableApplePay = isAdyenRestaurant && isApplePayAvailable

    const enableGooglePay = isAdyenRestaurant

    useEffect(() => {
        if (orderState?.adyen?.redirection) {
            const path = intl.formatMessage({
                id: 'routes.clickandcollect.cart.confirmation'
            })

            history.push(
                `${path}?id=${orderState?.adyen?.redirection.orderId}&token=${orderState?.adyen?.redirection.orderToken}`
            )
        }
    }, [history, orderState])

    const clearError = useCallback(() => {
        dispatch(actionAdyenRedirection(undefined))
        dispatch(actionAdyenPaymentResponse(null))
        setError(undefined)
    }, [dispatch])

    useEffect(() => {
        if (!authenticated || kingdomProfilePageState.email?.length > 0) {
            return
        }

        const fetchProfile = async () => {
            dispatch(
                setLoader({
                    isLoading: true,
                    intlKeyMessage:
                        'clickandcollect.waiting.order.fetchingProfile'
                })
            )
            await dispatch(fetchProfileData())

            dispatch(
                setLoader({
                    isLoading: false
                })
            )
        }

        fetchProfile()
    }, [authenticated, dispatch, kingdomProfilePageState.email])

    useEffect(() => {
        if (
            !submitData ||
            ([
                adyenPaymentMethods.GOOGLE_PAY,
                adyenPaymentMethods.APPLE_PAY
            ].includes(submitData.paymentMethod.type) &&
                !userData) ||
            orderState?.loader?.isLoading
        ) {
            return
        }

        const sendPayment = async () => {
            dispatch(
                setLoader({
                    isLoading: true,
                    intlKeyMessage: 'clickandcollect.waiting.order.validation'
                })
            )

            try {
                const paymentMethod: any = {
                    type: submitData.paymentMethod.type
                }

                if (
                    submitData.paymentMethod.type ===
                    adyenPaymentMethods.GOOGLE_PAY
                ) {
                    Object.assign(paymentMethod, {
                        googlePayToken: submitData.paymentMethod.googlePayToken
                    })
                }

                if (
                    submitData.paymentMethod.type ===
                    adyenPaymentMethods.APPLE_PAY
                ) {
                    Object.assign(paymentMethod, {
                        applePayToken: submitData.paymentMethod.applePayToken
                    })
                }

                const currentOrder: any = getCurrentOrder(
                    orderState?.cacOrders,
                    orderState?.deliveryOrders,
                    orderState?.currentToken
                )

                let userInfos = authenticated
                    ? {
                          email: kingdomProfilePageState.email.trim(),
                          firstname: kingdomProfilePageState.firstname.trim(),
                          phone: kingdomProfilePageState.phone
                      }
                    : {
                          firstname: '',
                          email: '',
                          phone: ''
                      }

                if (
                    submitData.paymentMethod.type ===
                    adyenPaymentMethods.GOOGLE_PAY
                ) {
                    // authenticated user infos are used as fallback except for email where it's reversed because an authenticated user is not supposed to change his email on the payment page
                    userInfos = {
                        email: userInfos.email || userData.email,
                        firstname:
                            // $FlowFixMe
                            userData?.shippingAddress?.name?.split(' ')?.[0] ||
                            userInfos.firstname,
                        phone:
                            userData?.shippingAddress.phoneNumber ||
                            userInfos.phone
                    }
                }

                if (
                    submitData.paymentMethod.type ===
                    adyenPaymentMethods.APPLE_PAY
                ) {
                    userInfos = {
                        // authenticated user infos are used as fallback except for email where it's reversed because an authenticated user is not supposed to change his email on the payment page
                        email: userInfos.email || userData.emailAddress,
                        firstname: userData?.givenName || userInfos.firstname,
                        phone: userData?.phoneNumber || userInfos.phone
                    }
                }

                // prevents from recreating an order if there is already one in progress
                if (
                    currentOrder?.token !== orderState?.currentToken ||
                    !orderState?.currentToken ||
                    ['PAID', 'REJECTED'].includes(currentOrder?.orderStatus)
                ) {
                    setShowCaptcha(true)
                    const recaptchaToken = await recaptchaPromise

                    const deviceCheckPayload = {
                        queen: recaptchaToken
                    }

                    await dispatch(
                        sendCurrentOrder({
                            userInfos,
                            deviceCheckPayload
                        })
                    )
                }

                await dispatch(
                    sendAdyenPayment({
                        paymentMethod,
                        browserInfo: submitData.browserInfo,
                        channel: 'Web',
                        customerEmail: userInfos.email
                    })
                )

                if (
                    submitData.paymentMethod.type ===
                    adyenPaymentMethods.APPLE_PAY
                ) {
                    // 0 => Apple pay success code
                    userData.applePayEvent(0)
                }
            } catch (error) {
                if (
                    submitData.paymentMethod.type ===
                        adyenPaymentMethods.APPLE_PAY &&
                    !authenticated
                ) {
                    // 1 => Apple pay error code
                    userData.applePayEvent(1)
                }
                dispatch(setError(error))
            } finally {
                setShowCaptcha(false)
                setUserData(undefined)
                setSubmitData(undefined)
                dispatch(setLoader({ isLoading: false }))
            }
        }

        sendPayment()
    }, [
        submitData,
        userData,
        kingdomProfilePageState.carColor,
        kingdomProfilePageState.email,
        kingdomProfilePageState.firstname,
        kingdomProfilePageState.licensePlateEnrolment,
        kingdomProfilePageState.licensePlateNumber,
        kingdomProfilePageState.phone,
        orderState
    ])

    useEffect(() => {
        initRecaptchaPromise()
        setShowCaptcha(true)
    }, [])

    useEffect(() => {
        if (
            !orderState?.adyen?.paymentMethods?.paymentMethods &&
            isAdyenRestaurant &&
            orderState?.restaurant?.id
        ) {
            dispatch(fetchAdyenPaymentMethods(orderState?.restaurant?.id))
        }
    }, [dispatch, orderState])

    const initRecaptchaPromise = () => {
        recaptchaPromise = new Promise((resolve, reject) => {
            resolvePromise = resolve
            rejectPromise = reject
        })
    }

    const captureResponseToken = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            resolvePromise(recaptchaToken)
            return
        }
        rejectPromise('Error getting recaptcha token')
    }

    const handleSubmit = useCallback(
        async ({ data }) => {
            if (!data.paymentMethod) {
                return
            }
            setSubmitData(data)
        },
        [dispatch]
    )

    useEffect(() => {
        if (
            !!checkout ||
            (!enableApplePay && !enableGooglePay) ||
            !orderState.adyen.paymentMethods
        )
            return

        const createCheckout = async () => {
            const AdyenConfiguration = {
                locale: 'fr-FR',
                countryCode: 'FR',
                environment: process.env.REACT_APP_ADYEN_ENVIRONNEMENT,
                clientKey: process.env.REACT_APP_ADYEN_CLIENT_KEY,
                paymentMethodsResponse: orderState.adyen.paymentMethods,
                onSubmit: handleSubmit,
                onError: (error, _component) => {
                    dispatch(setError(error))
                }
            }

            const adyenCheckout = await AdyenWebCheckout({
                ...AdyenConfiguration
            })
            setCheckout(adyenCheckout)
        }

        createCheckout()
    }, [orderState.adyen, checkout])

    const oneClickPaymentConfiguration = {
        onAuthorized: (data, _event, applePayPaymentAuthorizedEvent) => {
            setUserData({
                ...data, // used by google pay
                applePayEvent: data, // used by apple pay
                ...applePayPaymentAuthorizedEvent?.payment?.shippingContact
            })
        },
        countryCode: 'FR',
        environment: process.env.REACT_APP_ADYEN_ENVIRONNEMENT,
        amount: {
            value: (priceState.finalPriceWithDeliveryFees ?? 0) * 100,
            currency: 'EUR'
        }
    }

    // Mount Apple pay component
    useApplePay({
        isEnabled: enableApplePay,
        checkout,
        configuration: oneClickPaymentConfiguration
    })

    // Mount Google pay component
    useGooglePay({
        isEnabled: enableGooglePay,
        checkout,
        configuration: oneClickPaymentConfiguration
    })

    const errorMessage =
        error &&
        error.message &&
        intl.formatMessage({
            id: error.message
        })
    const errorReason =
        (error && error.customReason) ||
        (error &&
            error.reason &&
            intl.formatMessage({
                id: error.reason
            }))

    return (
        <>
            {!!errorMessage && (
                <ErrorBanner
                    isCart
                    text={errorMessage}
                    description={errorReason}
                    onClose={clearError}
                />
            )}

            <CartSummary {...props} />

            {showCaptcha && (
                <Recaptcha
                    action="AdyenOneClickPayment"
                    verifyCallback={captureResponseToken}
                />
            )}
        </>
    )
}
